import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { useServiceFeatures } from 'hooks/useServiceContext';
import { IntegrationCore } from 'types/Integration';
import { Undef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';

import {
  LTLAccessorial,
  QuickQuoteInputs,
  TransportTypeOption,
} from '../types';
import { UnifiedTaiLTLForm } from './Tai/UnifiedTaiLTLForm';
import { UnifiedThreeGLTLForm } from './ThreeG/UnifiedThreeGLTLForm';
import { UnifiedTurvoLTLForm } from './Turvo/UnifiedTurvoLTLForm';

function getTaiLikeIntegrationName(
  ltlTMSIntegrations: IntegrationCore[]
): Undef<TMS> {
  if (
    ltlTMSIntegrations.some(
      (integration) => integration.name === TMS.McleodEnterprise
    )
  ) {
    return TMS.McleodEnterprise;
  }

  if (ltlTMSIntegrations.some((integration) => integration.name === TMS.Tai)) {
    return TMS.Tai;
  }

  return undefined;
}

export interface LTLFormRouterProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  hasAddedQuoteDates: boolean;
  accessorials: LTLAccessorial[];
  isLoadingAccessorials?: boolean;
  transportTypeOptions: TransportTypeOption[];
  tmsIntegrations?: IntegrationCore[];
}

/**
 * Routes to integration LTL forms similar to load building's unified forms.
 * Each integration has its own self-contained sections like quote info,
 * stops, commodity, items, and accessorials.
 */
export function LTLFormRouter({
  formMethods,
  hasAddedQuoteDates,
  accessorials,
  isLoadingAccessorials = false,
  transportTypeOptions,
  tmsIntegrations,
}: LTLFormRouterProps) {
  const { ltlTMSIntegrations } = useServiceFeatures();

  const isThreeG = useMemo(() => {
    return ltlTMSIntegrations?.some(
      (integration) => integration.name === TMS.ThreeG
    );
  }, [ltlTMSIntegrations]);

  const isTurvo = useMemo(() => {
    return ltlTMSIntegrations?.some(
      (integration) => integration.name === TMS.Turvo
    );
  }, [ltlTMSIntegrations]);

  if (!ltlTMSIntegrations?.length) {
    return null;
  }

  const commonProps = {
    formMethods,
    hasAddedQuoteDates,
    accessorials,
    isLoadingAccessorials,
  };

  if (isThreeG) {
    return <UnifiedThreeGLTLForm {...commonProps} />;
  }
  if (isTurvo) {
    return (
      <UnifiedTurvoLTLForm
        formMethods={formMethods}
        transportTypeOptions={transportTypeOptions}
        tmsIntegrations={tmsIntegrations}
      />
    );
  }

  const activeTaiLikeIntegrationName =
    getTaiLikeIntegrationName(ltlTMSIntegrations);

  return (
    <UnifiedTaiLTLForm
      {...commonProps}
      integrationName={activeTaiLikeIntegrationName}
    />
  );
}
